import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Loader2,
  Mail,
  Phone,
  Send,
  ShieldCheck,
  User,
  UserRoundCog,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { getOrganizationTranslation } from "../context/organizationOnboardingTranslations";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const RequestAccount = () => {
  const { t, lang } = useLanguage();
  const tr = (key, fallback) =>
    t?.ui?.requestAccount?.[key] ||
    t?.[key] ||
    getOrganizationTranslation(lang, "requestAccount", key, fallback);

  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    accountType: "Client",
    name: "",
    email: "",
    phone: "",
    organizationId: "",
    requestedOrganizationName: "",
    role: "employee",
    reason: "",
  });

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/organizations/public`);
        const data = await response.json().catch(() => []);
        if (!response.ok) throw new Error(data.message || "Unable to load organizations");
        setOrganizations(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setMessage(
          lang === "en"
            ? "Unable to load the organization list."
            : "Impossible de charger la liste des organisations."
        );
      } finally {
        setLoadingOrganizations(false);
      }
    };
    loadOrganizations();
  }, [lang]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const chooseType = (accountType) => {
    setForm((current) => ({
      ...current,
      accountType,
      organizationId: "",
      requestedOrganizationName: "",
    }));
    setMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    setSuccess(false);

    try {
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || tr("genericError", "Une erreur est survenue."));

      setSuccess(true);
      setMessage(
        data.message ||
          (form.accountType === "Admin"
            ? tr("successAdmin", "Votre demande Administrateur a été envoyée au Super Admin.")
            : tr("successClient", "Votre demande Client a été envoyée à l’administrateur de l’organisation."))
      );
      setForm((current) => ({
        ...current,
        name: "",
        email: "",
        phone: "",
        organizationId: "",
        requestedOrganizationName: "",
        reason: "",
      }));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100";

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70">
        <header className="relative overflow-hidden bg-slate-950 px-6 py-10 text-white sm:px-10">
          <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-600/30 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-2 text-xs font-black text-cyan-200 ring-1 ring-white/10">
              <ShieldCheck className="h-4 w-4" />
              {tr("controlledAccess", "Accès contrôlé")}
            </div>
            <h1 className="mt-5 text-3xl font-black tracking-tight sm:text-4xl">
              {tr("title", "Demander un accès")}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300">
              {tr(
                "intro",
                "Choisissez le type de compte. Les demandes Client sont transmises à l’administrateur de l’organisation sélectionnée."
              )}
            </p>
          </div>
        </header>

        <section className="p-6 sm:p-10">
          {message && (
            <div
              className={`mb-6 flex items-start gap-3 rounded-2xl border p-4 text-sm font-semibold ${
                success
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-rose-200 bg-rose-50 text-rose-800"
              }`}
            >
              {success ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <AlertCircle className="h-5 w-5 shrink-0" />}
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-7">
            <div>
              <p className="text-sm font-black text-slate-900">
                {tr("accountType", "Type de compte")} *
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {[
                  ["Client", Building2, tr("clientAccount", "Compte Client"), tr("clientHelp", "Rejoindre une organisation existante")],
                  ["Admin", UserRoundCog, tr("adminAccount", "Compte Administrateur"), tr("adminHelp", "Demander un nouvel environnement d’organisation")],
                ].map(([value, Icon, label, help]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => chooseType(value)}
                    className={`rounded-2xl border p-5 text-left transition ${
                      form.accountType === value
                        ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                        : "border-slate-200 bg-white hover:border-blue-200"
                    }`}
                  >
                    <Icon className={`h-6 w-6 ${form.accountType === value ? "text-blue-700" : "text-slate-500"}`} />
                    <p className="mt-4 font-black text-slate-950">{label}</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">{help}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="mb-2 block text-sm font-bold text-slate-700">{tr("fullName", "Nom complet")} *</span>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input name="name" value={form.name} onChange={updateField} required className={`${inputClass} pl-11`} />
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">{tr("email", "Adresse email")} *</span>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input name="email" type="email" value={form.email} onChange={updateField} required className={`${inputClass} pl-11`} />
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">{tr("phone", "Téléphone")}</span>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input name="phone" value={form.phone} onChange={updateField} className={`${inputClass} pl-11`} />
                </div>
              </label>

              {form.accountType === "Client" ? (
                <label className="block sm:col-span-2">
                  <span className="mb-2 block text-sm font-bold text-slate-700">
                    {tr("selectOrganization", "Organisation à rejoindre")} *
                  </span>
                  <select
                    name="organizationId"
                    value={form.organizationId}
                    onChange={updateField}
                    required
                    disabled={loadingOrganizations}
                    className={inputClass}
                  >
                    <option value="">
                      {loadingOrganizations
                        ? tr("loadingOrganizations", "Chargement des organisations...")
                        : tr("chooseOrganization", "Choisir une organisation")}
                    </option>
                    {organizations.map((organization) => (
                      <option key={organization._id} value={organization._id}>
                        {organization.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    {tr("clientApprovalInfo", "L’administrateur de cette organisation recevra votre demande.")}
                  </p>
                </label>
              ) : (
                <>
                  <label className="block sm:col-span-2">
                    <span className="mb-2 block text-sm font-bold text-slate-700">
                      {tr("newOrganization", "Nom de la nouvelle organisation")} *
                    </span>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        name="requestedOrganizationName"
                        value={form.requestedOrganizationName}
                        onChange={updateField}
                        required
                        className={`${inputClass} pl-11`}
                      />
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      {tr("superAdminApprovalInfo", "Cette demande sera examinée par le Super Admin de la plateforme.")}
                    </p>
                  </label>

                </>
              )}

              <label className="block sm:col-span-2">
                <span className="mb-2 block text-sm font-bold text-slate-700">{tr("role", "Votre fonction")} *</span>
                <select name="role" value={form.role} onChange={updateField} className={inputClass}>
                  <option value="owner">{tr("roleOwner", "Propriétaire / Direction")}</option>
                  <option value="manager">{tr("roleManager", "Manager")}</option>
                  <option value="hr">{tr("roleHr", "Ressources humaines")}</option>
                  <option value="it">{tr("roleIt", "IT / Sécurité")}</option>
                  <option value="compliance">{tr("roleCompliance", "Conformité")}</option>
                  <option value="employee">{tr("roleEmployee", "Employé")}</option>
                </select>
              </label>

              <label className="block sm:col-span-2">
                <span className="mb-2 block text-sm font-bold text-slate-700">{tr("reason", "Motif de la demande")}</span>
                <textarea
                  name="reason"
                  value={form.reason}
                  onChange={updateField}
                  rows={4}
                  className={`${inputClass} resize-none`}
                />
              </label>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <Link to="/login" className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
                {tr("backLogin", "Retour à la connexion")}
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                {submitting ? tr("sending", "Envoi...") : tr("send", "Envoyer la demande")}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

export default RequestAccount;
